// React hooks
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  InvoiceIncomingRejection,
  InvoiceIncomingRow,
  InvoiceIncomingSearchFormValues,
} from "services/api/finance/incoming-invoices/types";
import { initialInvoiceIncomingSearchForm } from "services/api/finance/incoming-invoices/consts";

// API
import {
  getInvoiceIncomingsAPI,
  deleteInvoiceIncomingAPI,
  approveInvoiceIncomingAPI,
  rejectInvoiceIncomingAPI,
  markPaidInvoiceIncomingAPI,
} from "services/api/finance/incoming-invoices/api";

// UI komponente
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { Dialog } from "ui/components/Dialog";
import { Button } from "ui/components/Button/Button";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";

// Lokalne komponente
import InvoiceIncomingPreviewCard from "../InvoiceIncomingPreviewCard/InvoiceIncomingPreviewCard";
import InvoiceIncomingEditCard from "ui/modules/finance/pages/invoices/InvoiceIncomingEditCard/InvoiceIncomingEditCard";
import InvoiceIncomingRejectModal from "../InvoiceIncomingRejectModal/InvoiceIncomingRejectModal";
import InvoiceIncomingSearchFilters from "../InvoiceIncomingSearchFilters/InvoiceIncomingSearchFilters";
import {
  INCOMING_INVOICE_SEARCH_COLUMNS,
  INCOMING_INVOICE_ITEM_CHILD_COLUMNS,
} from "./consts";
import InvoiceIncomingItemRowChild from "./InvoiceIncomingItemRowChild";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { InvoiceIncomingSearchTabProps } from "./types";

const InvoiceIncomingSearchTab: React.FC<InvoiceIncomingSearchTabProps> = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<InvoiceIncomingRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editInvoiceId, setEditInvoiceId] = useState<string | null>(null);
  const [rejectIds, setRejectIds] = useState<string[]>([]);
  const [rejectInvoiceNumber, setRejectInvoiceNumber] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<InvoiceIncomingSearchFormValues>(initialInvoiceIncomingSearchForm);
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination, filterValues?: InvoiceIncomingSearchFormValues) => {
    setIsLoading(true);
    const f = filterValues ?? filtersRef.current;
    try {
      const results = await getInvoiceIncomingsAPI(pagination, {
        search: f.search,
        statusId: f.statusId,
        incomingSourceId: f.incomingSourceId,
        allocationStatus: f.allocationStatus,
        invoiceDateFrom: f.invoiceDateFrom,
        invoiceDateTo: f.invoiceDateTo,
        dueDateFrom: f.dueDateFrom,
        dueDateTo: f.dueDateTo,
        totalMin: f.totalMin,
        totalMax: f.totalMax,
      });
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback((formValues: InvoiceIncomingSearchFormValues) => {
    setFilters(formValues);
    fetchSearchResult(searchResults.pagination, formValues);
  }, [searchResults.pagination]);

  const handleRowClick = useCallback(async (id: string) => {
    // Ručno uneseni računi se otvaraju na uređivanje, e-računi ostaju samo za pregled.
    const row = searchResults.data.find((invoice) => invoice.id === id);

    if (row?.incomingSourceId === "manual") {
      setEditInvoiceId(id);
      setShowEditModal(true);
      return;
    }

    setInvoiceId(id);
    setShowAddNewModal(true);
  }, [searchResults.data]);

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setInvoiceId("");
    setShowAddNewModal(false);
  }, []);

  const handlePreviewCardClose = useCallback(() => {
    setShowAddNewModal(false);
  }, []);

  const handleNewInvoiceClick = useCallback(() => {
    setEditInvoiceId(null);
    setShowEditModal(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setEditInvoiceId(null);
  }, []);

  const handleEditModalSuccess = useCallback(() => {
    setShowEditModal(false);
    setEditInvoiceId(null);
    fetchSearchResult(searchResults.pagination);
  }, [searchResults.pagination]);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setInvoiceId(id);
    setOpenDialog(true);
  };

  const handleApproveClick = useCallback(async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await approveInvoiceIncomingAPI(id, t);
      fetchSearchResult(searchResults.pagination);
    } catch (err) {
      console.error("Greška prilikom odobrenja:", err);
    }
  }, [t]);

  const handleRejectClick = useCallback(async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    // Odbijanje e-računa se fiskalizira kod Porezne, pa razlog tražimo prije slanja.
    const row = searchResults.data.find((invoice) => invoice.id === id);
    if (row?.incomingSourceId === "provider") {
      setRejectIds([id]);
      setRejectInvoiceNumber(row.invoiceNumber ?? null);
      return;
    }

    try {
      await rejectInvoiceIncomingAPI(id, null, t);
      fetchSearchResult(searchResults.pagination);
    } catch (err) {
      console.error("Greška prilikom odbijanja:", err);
    }
  }, [searchResults.data, t]);

  const handleMarkPaidClick = useCallback(async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await markPaidInvoiceIncomingAPI(id, t);
      fetchSearchResult(searchResults.pagination);
    } catch (err) {
      console.error("Greška prilikom označavanja plaćenim:", err);
    }
  }, [t]);

  const handleInvoiceDeleteDialogConfirm = useCallback(async () => {
    if (!invoiceId) return;

    try {
      await deleteInvoiceIncomingAPI(invoiceId, t);
      fetchSearchResult(searchResults.pagination);
      setOpenDialog(false);
      setInvoiceId("");
    } catch (err) {
      console.error("Greška prilikom brisanja računa:", err);
    }
  }, [invoiceId, t]);

  const handleInvoiceDeleteDialogClose = useCallback(() => {
    setInvoiceId("");
    setOpenDialog(false);
  }, []);

  const selectedInvoices = useMemo(() => {
    const idSet = new Set(selectedRows.map(String));
    return searchResults.data.filter((row) => idSet.has(String(row.id)));
  }, [selectedRows, searchResults.data]);

  const selectedReceivedIds = useMemo(() => {
    return selectedInvoices.filter((row) => row.status?.id === "received").map((row) => row.id);
  }, [selectedInvoices]);

  const hasSelectedProviderInvoices = useMemo(() => {
    return selectedInvoices.some((row) => row.status?.id === "received" && row.incomingSourceId === "provider");
  }, [selectedInvoices]);

  const selectedIds = useMemo(() => {
    return selectedInvoices.map((row) => row.id);
  }, [selectedInvoices]);

  const [bulkAction, setBulkAction] = useState<"approve" | "reject" | "delete" | null>(null);

  const handleBulkDialogConfirm = useCallback(async () => {
    if (!bulkAction) return;
    setIsLoading(true);
    try {
      switch (bulkAction) {
        case "approve":
          await Promise.all(selectedReceivedIds.map((id: string) => approveInvoiceIncomingAPI(id, t)));
          break;
        case "reject":
          await Promise.all(selectedReceivedIds.map((id: string) => rejectInvoiceIncomingAPI(id, null, t)));
          break;
        case "delete":
          await Promise.all(selectedIds.map((id: string) => deleteInvoiceIncomingAPI(id, t)));
          break;
      }
      setSelectedRows([]);
      fetchSearchResult(searchResults.pagination);
    } catch {}
    setBulkAction(null);
    setIsLoading(false);
  }, [bulkAction, selectedReceivedIds, selectedIds, searchResults, t]);

  const handleBulkRejectClick = useCallback(() => {
    // Za e-račune razlog je obavezan — isti razlog primjenjujemo na sve odabrane.
    if (hasSelectedProviderInvoices) {
      setRejectIds(selectedReceivedIds);
      setRejectInvoiceNumber(null);
      return;
    }

    setBulkAction("reject");
  }, [hasSelectedProviderInvoices, selectedReceivedIds]);

  const handleRejectModalHide = useCallback(() => {
    setRejectIds([]);
    setRejectInvoiceNumber(null);
  }, []);

  const handleRejectConfirm = useCallback(async (rejection: InvoiceIncomingRejection) => {
    setIsLoading(true);

    try {
      await Promise.all(rejectIds.map((id: string) => rejectInvoiceIncomingAPI(id, rejection, t)));
      setRejectIds([]);
      setRejectInvoiceNumber(null);
      setSelectedRows([]);
      fetchSearchResult(searchResults.pagination);
    } catch {
      // greška se prikazuje putem snackbara
    }

    setIsLoading(false);
  }, [rejectIds, searchResults.pagination, t]);

  const handleBulkApproveClick = useCallback(() => {
    setBulkAction("approve");
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    setBulkAction("delete");
  }, []);

  const handleBulkDialogCancel = useCallback(() => {
    setBulkAction(null);
  }, []);

  const handleContactClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id }));
  };

  return (
    <>
      <div className="px-0 py-6 mx-[-1px] xl:px-6">
        <div className="flex items-end justify-between mb-3">
          <InvoiceIncomingSearchFilters onSearchChange={handleSearchChange} />
          <div className="flex items-end gap-2">
            {selectedRows.length > 0 && (
              <ButtonMenu
                text={`Grupne akcije (${selectedRows.length})`}
                variant="secondary"
                size="full"
              >
                {selectedReceivedIds.length > 0 && (
                  <ButtonMenuItem
                    startIcon="/media/icons/check.svg"
                    onClick={handleBulkApproveClick}
                  >
                    Odobri zaprimljene ({selectedReceivedIds.length})
                  </ButtonMenuItem>
                )}
                {selectedReceivedIds.length > 0 && (
                  <ButtonMenuItem
                    startIcon="/media/icons/close.svg"
                    onClick={handleBulkRejectClick}
                  >
                    Odbij zaprimljene ({selectedReceivedIds.length})
                  </ButtonMenuItem>
                )}
                <ButtonMenuItem
                  startIcon="/media/icons/trash.svg"
                  variant="remove"
                  onClick={handleBulkDeleteClick}
                >
                  Obriši odabrane ({selectedRows.length})
                </ButtonMenuItem>
              </ButtonMenu>
            )}
            <Button
              variant="primary"
              children="Kreiraj ulazni račun"
              onClick={handleNewInvoiceClick}
            />
          </div>
        </div>

        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message="Nema rezultata" />
        ) : (
          <TableSearch
            columns={INCOMING_INVOICE_SEARCH_COLUMNS(
              t,
              handleApproveClick,
              handleRejectClick,
              handleMarkPaidClick,
              handleDeleteClick,
              handleContactClick
            )}
            data={searchResults.data}
            selectedRows={selectedRows}
            pagination={
              searchResults.pagination || initialDataWithPagination.pagination
            }
            selectableRows={true}
            onPaginationChange={handlePaginationChange}
            onSelectedChange={handleSelectedChange}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            columnsChild={INCOMING_INVOICE_ITEM_CHILD_COLUMNS()}
            CustomTableRowChild={InvoiceIncomingItemRowChild}
            childTableTitle="Stavke računa"
          />
        )}
      </div>

      <Dialog
        open={openDialog}
        title="Brisanje ulaznog računa"
        message="Jeste li sigurni da želite obrisati ovaj ulazni račun?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleInvoiceDeleteDialogConfirm}
        onCancel={handleInvoiceDeleteDialogClose}
      />

      {/* Skupne radnje imaju svaka svoj dijalog — tekstovi su različiti, a i broj računa nad kojim
          rade (odobrenje i odbijanje idu samo na zaprimljene). */}
      <Dialog
        open={bulkAction === "approve"}
        title={`Odobrenje ${selectedReceivedIds.length} računa`}
        message={`Jeste li sigurni da želite odobriti ${selectedReceivedIds.length} odabranih računa?`}
        confirmText="Odobri"
        cancelText="Odustani"
        onConfirm={handleBulkDialogConfirm}
        onCancel={handleBulkDialogCancel}
      />

      <Dialog
        open={bulkAction === "reject"}
        title={`Odbijanje ${selectedReceivedIds.length} računa`}
        message={`Jeste li sigurni da želite odbiti ${selectedReceivedIds.length} odabranih računa?`}
        confirmText="Odbij"
        cancelText="Odustani"
        onConfirm={handleBulkDialogConfirm}
        onCancel={handleBulkDialogCancel}
      />

      <Dialog
        open={bulkAction === "delete"}
        title={`Brisanje ${selectedIds.length} računa`}
        message={`Jeste li sigurni da želite obrisati ${selectedIds.length} odabranih računa?`}
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleBulkDialogConfirm}
        onCancel={handleBulkDialogCancel}
      />

      <InvoiceIncomingRejectModal
        show={rejectIds.length > 0}
        onHide={handleRejectModalHide}
        onConfirm={handleRejectConfirm}
        invoiceNumber={rejectInvoiceNumber}
      />

      <InvoiceIncomingPreviewCard
        show={showAddNewModal}
        invoiceId={invoiceId || null}
        onSuccess={handleEditSuccess}
        onClose={handlePreviewCardClose}
      />

      <InvoiceIncomingEditCard
        show={showEditModal}
        invoiceId={editInvoiceId}
        onSuccess={handleEditModalSuccess}
        onClose={handleEditModalClose}
      />
    </>
  );
};

export default InvoiceIncomingSearchTab;
